import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Radio, X, Send, Users, UserPlus, Video, VideoOff, CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RTC_CONFIG, waitIceComplete } from "@/lib/webrtc";
import { LiveReactionsLayer } from "./LiveReactions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LiveBroadcastDialog — used by the streamer.
 * Captures webcam via getUserMedia, creates one RTCPeerConnection per viewer
 * and signals offers/answers/ICE via backend HTTP polling.
 */
export default function LiveBroadcastDialog({ open, onOpenChange, onStarted, onEnded, onRecordingPublished, onScheduled, scheduled }) {
  const [title, setTitle] = useState("Transmissão ao vivo");
  const [recordEnabled, setRecordEnabled] = useState(true);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [scheduling, setScheduling] = useState(false);

  // When opened from an "Iniciar agora" button of a scheduled live, prefill title/record
  useEffect(() => {
    if (open && scheduled) {
      setTitle(scheduled.title || "Transmissão ao vivo");
      setRecordEnabled(scheduled.record !== false);
      setScheduleMode(false);
    }
  }, [open, scheduled]);

  const scheduleLive = async () => {
    if (!scheduleAt) { toast.error("Escolha data e hora"); return; }
    const when = new Date(scheduleAt);
    if (Number.isNaN(when.getTime()) || when.getTime() < Date.now() + 60_000) {
      toast.error("Escolha um horário no futuro");
      return;
    }
    setScheduling(true);
    try {
      const r = await axios.post(`${API}/lives/schedule`, { title, scheduled_at: when.toISOString(), record: recordEnabled });
      const n = r.data?.notified_followers || 0;
      toast.success(`Live agendada para ${when.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`, {
        description: n ? `${n} seguidor(es) avisado(s). Todos receberão um lembrete 10 min antes.` : "Seus seguidores receberão um lembrete 10 min antes.",
      });
      onScheduled?.(r.data);
      setScheduleMode(false);
      setScheduleAt("");
      onOpenChange(false);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erro ao agendar live");
    } finally {
      setScheduling(false);
    }
  };
  const [live, setLive] = useState(null);
  const [chat, setChat] = useState([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [reactions, setReactions] = useState([]);
  const [reactionTotals, setReactionTotals] = useState({});
  const [chatInput, setChatInput] = useState("");
  const [starting, setStarting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [friends, setFriends] = useState([]);
  const [selected, setSelected] = useState(new Set());

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const pollRef = useRef(null);
  const rtcPollRef = useRef(null);
  const peersRef = useRef({}); // viewer_id -> { pc, answered, addedCandidates:Set }
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordStartRef = useRef(0);
  const liveRef = useRef(null);
  const [savingRecording, setSavingRecording] = useState(false);

  // Auto-record the broadcast so it can be published as a replay video
  const startRecording = (stream) => {
    if (typeof MediaRecorder === "undefined" || !stream) return;
    try {
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus"
        : (MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "");
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime, videoBitsPerSecond: 800_000 } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.start(1000);
      recorderRef.current = rec;
      recordStartRef.current = Date.now();
    } catch (err) {
      console.warn("[LiveBroadcast] MediaRecorder unavailable", err?.message);
      recorderRef.current = null;
    }
  };

  const stopRecording = () => new Promise((resolve) => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (!rec || rec.state === "inactive") { resolve(null); return; }
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || "video/webm" });
      chunksRef.current = [];
      resolve(blob.size > 0 ? blob : null);
    };
    try { rec.stop(); } catch { resolve(null); }
  });

  const publishRecording = async (liveId, blob) => {
    if (!blob) return;
    if (blob.size > 50 * 1024 * 1024) {
      toast.warning("A gravação ficou muito grande (limite 50MB) e não pôde ser salva.");
      return;
    }
    setSavingRecording(true);
    const tid = toast.loading("Salvando gravação da live no seu perfil...");
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `live-${liveId}.webm`, { type: "video/webm" }));
      const up = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const duration = Math.max(1, Math.round((Date.now() - recordStartRef.current) / 1000));
      const r = await axios.post(`${API}/lives/${liveId}/recording`, { video_url: up.data.url, duration });
      toast.success("Gravação da live publicada no seu perfil!", { id: tid });
      onRecordingPublished?.(r.data);
    } catch (err) {
      console.error("[LiveBroadcast] publishRecording failed", err);
      toast.error("Não foi possível salvar a gravação da live", { id: tid });
    } finally {
      setSavingRecording(false);
    }
  };

  // Cleanup all peer connections
  const closePeers = () => {
    Object.values(peersRef.current).forEach((peer) => {
      try { peer.pc.close(); }
      catch (err) { console.debug("[LiveBroadcast] closePeers pc.close", err?.message); }
    });
    peersRef.current = {};
  };

  // Create an offer for a new viewer
  const createOfferForViewer = async (liveId, viewerId) => {
    if (peersRef.current[viewerId]) return;
    const pc = new RTCPeerConnection(RTC_CONFIG);
    peersRef.current[viewerId] = { pc, answered: false, addedCandidates: new Set() };
    try {
      streamRef.current?.getTracks().forEach((tr) => pc.addTrack(tr, streamRef.current));
      const offer = await pc.createOffer({ offerToReceiveAudio: false, offerToReceiveVideo: false });
      await pc.setLocalDescription(offer);
      await waitIceComplete(pc);
      await axios.post(`${API}/lives/${liveId}/offer/${viewerId}`, {
        sdp: pc.localDescription.sdp,
        type: pc.localDescription.type,
      });
    } catch (err) {
      console.error("[LiveBroadcast] createOfferForViewer failed", err);
      try { pc.close(); }
      catch (closeErr) { console.debug("[LiveBroadcast] cleanup pc.close", closeErr?.message); }
      delete peersRef.current[viewerId];
    }
  };

  // Poll for answers + ICE from viewers, and reconcile viewers list
  const rtcTick = async (liveId) => {
    // Get current viewer list
    let viewerIds = [];
    try {
      const r = await axios.get(`${API}/lives/${liveId}/viewers`);
      viewerIds = r.data.viewer_ids || [];
    } catch { return; }

    // Remove peers that left
    Object.keys(peersRef.current).forEach((vid) => {
      if (!viewerIds.includes(vid)) {
        try { peersRef.current[vid].pc.close(); }
        catch (err) { console.debug("[LiveBroadcast] leaver pc.close", err?.message); }
        delete peersRef.current[vid];
      }
    });

    // Create new peers for new viewers
    for (const vid of viewerIds) {
      if (!peersRef.current[vid]) await createOfferForViewer(liveId, vid);
    }

    // For each active peer, poll answer + candidates
    for (const vid of Object.keys(peersRef.current)) {
      const peer = peersRef.current[vid];
      try {
        const r = await axios.get(`${API}/lives/${liveId}/answer/${vid}`);
        const ans = r.data.answer;
        if (ans && !peer.answered && peer.pc.signalingState !== "closed") {
          await peer.pc.setRemoteDescription({ type: ans.type, sdp: ans.sdp });
          peer.answered = true;
        }
        const cands = r.data.candidates || [];
        for (const c of cands) {
          if (!c || !c.candidate) continue;
          const key = `${c.candidate}|${c.sdpMid}|${c.sdpMLineIndex}`;
          if (peer.addedCandidates.has(key)) continue;
          peer.addedCandidates.add(key);
          try { await peer.pc.addIceCandidate(c); }
          catch (err) { console.debug("[LiveBroadcast] addIceCandidate skipped", err?.message); }
        }
      } catch (err) {
        console.error("[LiveBroadcast] rtcTick peer poll failed", err);
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const startCamera = async () => {
    // Pre-flight: check HTTPS + API existence
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Seu navegador não suporta câmera. Use Chrome, Firefox ou Safari atualizado.");
      return false;
    }
    if (window.isSecureContext === false) {
      toast.error("A transmissão só funciona em HTTPS. Recarregue o app pela URL segura.");
      return false;
    }
    // Try video+audio first, fall back to video-only, then audio-only
    const tryConstraints = async (constraints) => {
      try { return await navigator.mediaDevices.getUserMedia(constraints); }
      catch (e) { return e; }
    };
    let s = await tryConstraints({ video: true, audio: true });
    if (s instanceof Error) {
      console.warn("[Live] getUserMedia video+audio failed, retrying video-only", s.name, s.message);
      s = await tryConstraints({ video: true, audio: false });
    }
    if (s instanceof Error) {
      const err = s;
      console.error("[Live] getUserMedia failed", err.name, err.message);
      const map = {
        NotAllowedError: "Permissão de câmera negada. Habilite nas configurações do navegador.",
        PermissionDeniedError: "Permissão de câmera negada. Habilite nas configurações do navegador.",
        NotFoundError: "Nenhuma câmera encontrada neste dispositivo.",
        DevicesNotFoundError: "Nenhuma câmera encontrada neste dispositivo.",
        NotReadableError: "Câmera está sendo usada por outro aplicativo. Feche-o e tente novamente.",
        TrackStartError: "Câmera está sendo usada por outro aplicativo. Feche-o e tente novamente.",
        OverconstrainedError: "Configuração de câmera incompatível.",
        SecurityError: "Bloqueado por segurança. Verifique se o site é HTTPS.",
        AbortError: "Acesso à câmera interrompido. Tente novamente.",
      };
      toast.error(map[err.name] || `Erro ao acessar câmera: ${err.message || err.name || "desconhecido"}`);
      return false;
    }
    streamRef.current = s;
    if (videoRef.current) {
      videoRef.current.srcObject = s;
      videoRef.current.play().catch(() => {});
    }
    return true;
  };

  const startLive = async () => {
    setStarting(true);
    const ok = await startCamera();
    if (!ok) { setStarting(false); return; }
    try {
      const r = await axios.post(`${API}/lives/start`, { title, record: recordEnabled, scheduled_id: scheduled?.scheduled_id || null });
      setLive(r.data);
      liveRef.current = r.data;
      if (recordEnabled) startRecording(streamRef.current);
      toast.success(recordEnabled ? "Você está ao vivo! A transmissão está sendo gravada." : "Você está ao vivo! (sem gravação)");
      onStarted?.();
    } catch (err) {
      console.error("[Live] /lives/start failed", err);
      stopCamera();
      const msg = err.response?.data?.detail || err.message || "Erro ao iniciar transmissão";
      toast.error(typeof msg === "string" ? msg : "Erro ao iniciar transmissão");
    } finally {
      setStarting(false);
    }
  };

  const endLive = async () => {
    if (!live) return;
    const liveId = live.live_id;
    const blob = await stopRecording();
    try { await axios.post(`${API}/lives/${liveId}/end`); }
    catch (err) { console.error("[LiveBroadcast] end failed", err); }
    stopCamera();
    closePeers();
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (rtcPollRef.current) { clearInterval(rtcPollRef.current); rtcPollRef.current = null; }
    setLive(null);
    liveRef.current = null;
    setChat([]);
    setViewerCount(0);
    toast.info("Transmissão encerrada");
    onEnded?.();
    onOpenChange(false);
    await publishRecording(liveId, blob);
  };

  const sendMessage = async () => {
    if (!live || !chatInput.trim()) return;
    try {
      await axios.post(`${API}/lives/${live.live_id}/chat`, { message: chatInput.trim() });
      setChatInput("");
    } catch (err) {
      console.error("[LiveBroadcast] chat send failed", err);
    }
  };

  const loadFriends = async () => {
    try {
      const r = await axios.get(`${API}/users`);
      setFriends(r.data || []);
    } catch (err) {
      console.error("[LiveBroadcast] loadFriends failed", err);
    }
  };

  const sendInvites = async () => {
    if (!live || !selected.size) return;
    try {
      const r = await axios.post(`${API}/lives/${live.live_id}/invite`, { user_ids: Array.from(selected) });
      toast.success(`${r.data.sent} convite(s) enviado(s)!`);
      setInviteOpen(false);
      setSelected(new Set());
    } catch {
      toast.error("Erro ao enviar convites");
    }
  };

  // Poll live status + WebRTC signaling while broadcasting
  useEffect(() => {
    if (!live || pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/lives/${live.live_id}`);
        setChat(r.data.chat || []);
        setViewerCount(r.data.viewer_count || 0);
        setReactions(r.data.reactions || []);
        setReactionTotals(r.data.reaction_totals || {});
      } catch (err) { console.error("[LiveBroadcast] poll failed", err); }
    }, 3000);
    // WebRTC signaling tick (every 2s)
    rtcPollRef.current = setInterval(() => { rtcTick(live.live_id); }, 2000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (rtcPollRef.current) { clearInterval(rtcPollRef.current); rtcPollRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.live_id]);

  // Cleanup on unmount / close
  useEffect(() => {
    if (!open && (live || streamRef.current)) {
      // If closed while active, end it
      endLive();
    }
    return () => { if (!open) stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // If the streamer closes the tab, end the live server-side so it never lingers in "Ao vivo agora"
  useEffect(() => {
    const onPageHide = () => {
      const l = liveRef.current;
      if (!l) return;
      try {
        fetch(`${API}/lives/${l.live_id}/end`, {
          method: "POST",
          keepalive: true,
          headers: { Authorization: axios.defaults.headers.common["Authorization"] || "" },
        });
      } catch { /* best-effort */ }
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
    };
  }, []);

  // Reactions floating overlay (broadcaster sees viewers' reactions live)
  const broadcastReactionsCtl = LiveReactionsLayer({
    reactions,
    totals: reactionTotals,
    testIdPrefix: "live-broadcast",
  });

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v && live) endLive(); else onOpenChange(v); }}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="live-broadcast-dialog">
          <DialogHeader className="p-4 border-b border-border">
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <Radio className={`w-5 h-5 ${live ? "text-red-500 animate-pulse" : "text-muted-foreground"}`} />
              <span className="mr-auto">{live ? "Ao vivo agora" : "Iniciar transmissão ao vivo"}</span>
              {live && (
                <span
                  data-testid="live-broadcast-count-badge"
                  className="text-xs bg-red-500 text-white px-2.5 py-1 rounded-full flex items-center gap-1.5 font-semibold shadow-lg shadow-red-500/30"
                >
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" aria-hidden />
                  <Users className="w-3.5 h-3.5" />
                  <span data-testid="live-broadcast-count">{viewerCount}</span>
                  {viewerCount === 1 ? "espectador" : "espectadores"}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            <div className="md:col-span-2 p-4 space-y-3 bg-black">
              <div className="relative">
                <video
                  ref={videoRef}
                  data-testid="live-broadcast-video"
                  className="w-full aspect-video bg-black rounded-lg object-cover"
                  autoPlay
                  playsInline
                  muted
                />
                {/* Reactions floating overlay + totals */}
                {live && broadcastReactionsCtl.overlay}
                {live && recorderRef.current && (
                  <span data-testid="live-rec-indicator" className="absolute top-2 left-2 flex items-center gap-1.5 text-[11px] font-bold text-white bg-black/60 px-2 py-1 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> REC · será salva no seu perfil
                  </span>
                )}
                {savingRecording && (
                  <span data-testid="live-rec-saving" className="absolute bottom-2 left-2 text-[11px] font-semibold text-white bg-black/60 px-2 py-1 rounded-full">Salvando gravação...</span>
                )}
              </div>
              {!live && (
                <div className="bg-background rounded-lg p-3 space-y-2">
                  <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Título</label>
                  <Input
                    data-testid="live-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ex: Bate-papo com a comunidade"
                    maxLength={120}
                  />
                  <div
                    data-testid="live-record-toggle-row"
                    className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${recordEnabled ? "border-red-500/40 bg-red-500/5" : "border-border bg-muted/40"}`}
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      {recordEnabled ? <Video className="w-4 h-4 mt-0.5 text-red-500 flex-shrink-0" /> : <VideoOff className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold leading-tight">Gravar transmissão</div>
                        <div className="text-[11px] text-muted-foreground leading-snug">
                          {recordEnabled
                            ? "Ao encerrar, o vídeo será salvo e publicado no seu perfil. Os espectadores serão avisados."
                            : "A live não será gravada nem publicada depois."}
                        </div>
                      </div>
                    </div>
                    <Switch
                      data-testid="live-record-toggle"
                      checked={recordEnabled}
                      onCheckedChange={setRecordEnabled}
                      aria-label="Gravar transmissão"
                    />
                  </div>
                  {scheduleMode ? (
                    <div className="space-y-2 rounded-lg border border-border p-3" data-testid="live-schedule-panel">
                      <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1.5">
                        <CalendarClock className="w-3.5 h-3.5" /> Data e hora da live
                      </label>
                      <Input
                        data-testid="live-schedule-datetime"
                        type="datetime-local"
                        value={scheduleAt}
                        min={new Date(Date.now() + 2 * 60_000).toISOString().slice(0, 16)}
                        onChange={(e) => setScheduleAt(e.target.value)}
                      />
                      <p className="text-[11px] text-muted-foreground">Seus seguidores são avisados agora e recebem um lembrete 10 minutos antes de começar.</p>
                      <div className="flex gap-2">
                        <Button data-testid="live-schedule-cancel" variant="outline" className="flex-1 rounded-full" onClick={() => setScheduleMode(false)}>Voltar</Button>
                        <Button data-testid="live-schedule-confirm" className="flex-1 rounded-full" onClick={scheduleLive} disabled={scheduling || !scheduleAt || !title.trim()}>
                          <CalendarClock className="w-4 h-4 mr-2" /> {scheduling ? "Agendando..." : "Agendar live"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        data-testid="live-start-btn"
                        onClick={startLive}
                        disabled={starting || !title.trim()}
                        className="flex-1 rounded-full bg-red-500 hover:bg-red-600 text-white"
                      >
                        <Radio className="w-4 h-4 mr-2" />
                        {starting ? "Iniciando..." : (scheduled ? "Iniciar live agendada" : "Começar transmissão")}
                      </Button>
                      {!scheduled && (
                        <Button data-testid="live-schedule-btn" variant="outline" className="rounded-full" onClick={() => setScheduleMode(true)}>
                          <CalendarClock className="w-4 h-4 mr-2" /> Agendar
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {live && (
                <div className="flex gap-2">
                  <Button
                    data-testid="live-invite-btn"
                    onClick={() => { setInviteOpen(true); loadFriends(); }}
                    variant="outline"
                    className="flex-1 rounded-full"
                  >
                    <UserPlus className="w-4 h-4 mr-2" /> Convidar amigos
                  </Button>
                  <Button
                    data-testid="live-end-btn"
                    onClick={endLive}
                    className="flex-1 rounded-full bg-red-500 hover:bg-red-600 text-white"
                  >
                    <X className="w-4 h-4 mr-2" /> Encerrar
                  </Button>
                </div>
              )}
            </div>

            <div className="border-l border-border flex flex-col h-[70vh] md:h-auto">
              <div className="p-3 border-b border-border text-xs uppercase tracking-wider font-bold text-muted-foreground">
                Chat ao vivo
              </div>
              <ScrollArea className="flex-1 p-3">
                <div className="space-y-2" data-testid="live-broadcast-chat">
                  {chat.length === 0 && (
                    <div className="text-xs text-muted-foreground text-center py-8">
                      {live ? "Aguardando mensagens..." : "Comece a transmissão para receber mensagens."}
                    </div>
                  )}
                  {chat.map((m) => (
                    <div key={m.msg_id} className="flex items-start gap-2 text-sm">
                      <Avatar className="w-6 h-6 flex-shrink-0">
                        <AvatarImage src={m.author_picture} />
                        <AvatarFallback>{m.author_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold text-muted-foreground">{m.author_name}</div>
                        <div className="text-sm break-words">{m.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {live && (
                <div className="p-3 border-t border-border flex gap-2">
                  <Input
                    data-testid="live-broadcast-chat-input"
                    placeholder="Diga algo..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                  />
                  <Button data-testid="live-broadcast-chat-send" size="icon" onClick={sendMessage} className="rounded-full flex-shrink-0">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite modal */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md" data-testid="live-invite-dialog">
          <DialogHeader>
            <DialogTitle>Convidar amigos para assistir</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-72 pr-3">
            <div className="space-y-1">
              {friends.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Nenhum usuário disponível.</div>}
              {friends.map((f) => {
                const isSel = selected.has(f.user_id);
                return (
                  <button
                    key={f.user_id}
                    data-testid={`invite-friend-${f.user_id}`}
                    onClick={() => {
                      const nx = new Set(selected);
                      if (isSel) nx.delete(f.user_id); else nx.add(f.user_id);
                      setSelected(nx);
                    }}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-colors text-left ${isSel ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}
                  >
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={f.picture} />
                      <AvatarFallback>{f.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 text-sm font-medium truncate">{f.name}</div>
                    {isSel && <span className="text-xs text-primary font-bold">✓</span>}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setInviteOpen(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="live-invite-send-btn" onClick={sendInvites} disabled={!selected.size} className="rounded-full">
              <Send className="w-4 h-4 mr-2" /> Enviar ({selected.size})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
